import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PersonaStep from "../components/PersonaStep";
import SignInForm from "../components/SignInForm";
import SignupTrustStrip from "../components/SignupTrustStrip";
import ReferredByBadge from "../components/ReferredByBadge";
import { useUserAuth } from "../lib/userAuth";

import useDocumentTitle from "../hooks/useDocumentTitle";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";

/**
 * Phase 1 — multi-step signup.
 *   Step 1: email + password (shared <SignInForm>).
 *   Step 2: persona wizard (PersonaStep). On submit, calls signup() with
 *           the embedded persona payload so the account is born fully
 *           classified — single round-trip to the backend.
 *
 * Login mode skips Step 2 entirely (existing accounts already have or
 * will be soft-prompted for persona via PersonaPromptModal).
 *
 * iter 4.9 — credentials form extracted to <SignInForm> for parity with
 * the popover <SignInModal>.
 */
export default function SignIn() {
  const { t } = useTranslation();
  usePageMeta(M.signin);
  const { account, loading, signup, login } = useUserAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const rawNext = params.get("next") || "/account/billing";
  const next = (rawNext.startsWith("/") && !rawNext.startsWith("//")) ? rawNext : "/account/billing";
  // Iter94 — detect whether `next` was explicitly provided by the URL
  // (deep-linked signup gates) versus the SignIn fallback. Fresh signups
  // with NO explicit destination land on /account/quickstart instead of
  // billing, lifting onboarding-step-1 completion.
  const explicitNext = !!params.get("next");
  const wantsSignup =
    (params.get("mode") || "").toLowerCase() === "signup" ||
    location.pathname === "/signup";
  const initialMode = wantsSignup ? "signup" : "login";

  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1);
  // Carrier for credentials so Step 2 (persona) can complete signup atomically.
  const [pendingCreds, setPendingCreds] = useState(null);
  const [step2Error, setStep2Error] = useState("");
  const [busy, setBusy] = useState(false);
  // True once submitStep2 has issued its own navigate. Prevents the
  // "redirect when authed" useEffect below from racing past us with
  // a stale `next` destination after signup completes.
  const [navigated, setNavigated] = useState(false);
  const magicError = params.get("magic_error") || "";

  useEffect(() => {
    if (!loading && account && !navigated) navigate(next, { replace: true });
  }, [account, loading, navigate, next, navigated]);

  // Step 1 (login): submit credentials to /api/account/login.
  const onLogin = async ({ email, password, turnstile_token }) => {
    await login({ email, password, turnstile_token });
    navigate(next, { replace: true });
  };

  // Step 1 (signup): stash creds, advance to Step 2 (persona).
  const onSignupSubmit = async (creds) => {
    setPendingCreds(creds);
    setStep(2);
  };

  // Magic-link "trusted device, signed in instantly" → hard-navigate.
  const onMagicTrusted = () => {
    window.location.assign("/account?magic=trusted");
  };

  // Step 2: persona payload + atomic account creation.
  const submitStep2 = async (personaPayload) => {
    setStep2Error(""); setBusy(true);
    try {
      const creds = pendingCreds || {};
      await signup({
        email: creds.email,
        password: creds.password,
        name: creds.name,
        marketing_opt_in: creds.marketing_opt_in,
        persona: personaPayload,
        // Phase 12.1a — Turnstile token captured in step 1 carries
        // forward to the actual /signup network call here.
        turnstile_token: creds.turnstile_token,
      });
      // Iter94 — Custom-build enterprise: route directly into the (Phase 7
      // placeholder) custom-enterprise page so they immediately see we
      // received their intent.
      // Otherwise, if no explicit `next` was provided AND the persona has
      // a Quick Start track (individual/team/enterprise), land on
      // /account/quickstart so the brand-new user sees their tailored
      // onboarding path immediately. If they came in via a deep-link
      // `?next=...`, honor that — they had intent.
      const isCustomBuild = personaPayload.persona_type === "enterprise"
        && personaPayload.enterprise_track === "custom_build";
      const hasTrack = ["individual", "team", "enterprise"].includes(personaPayload.persona_type);
      let dest;
      if (isCustomBuild) {
        dest = "/account/custom-enterprise";
      } else if (!explicitNext && hasTrack) {
        dest = "/account/quickstart";
      } else {
        dest = next;
      }
      setNavigated(true);
      navigate(dest, { replace: true });
    } catch (err) {
      setStep2Error(err?.response?.data?.detail || err.message || "Could not create account.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen qt-radial-bg" data-testid="signin-page">
      <Header />
      <section className="max-w-[640px] mx-auto px-6 sm:px-8 py-16">
        <SignupTrustStrip />
        {mode === "signup" && <ReferredByBadge className="mb-4" />}
        <div className="qt-card qt-grain" data-testid="signin-card" style={{ border: "1px solid var(--qt-cyan)" }}>
          <div className="flex items-center gap-3">
            <Lock size={16} style={{ color: "var(--qt-cyan)" }} />
            <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
              {mode === "signup"
                ? (step === 1 ? t("auth.signup_eyebrow_step1", "STEP 1 OF 2 · YOUR CREDENTIALS")
                              : t("auth.signup_eyebrow_step2", "STEP 2 OF 2 · ABOUT YOU"))
                : t("auth.signin_eyebrow", "SIGN IN TO QORTRACE")}
            </div>
          </div>
          <h1 className="qt-h2 text-3xl sm:text-4xl mt-2">
            {mode === "signup"
              ? (step === 1 ? t("auth.signup_title", "Welcome to QorTrace.") : t("auth.signup_persona_title", "Help us tailor QorTrace."))
              : t("auth.signin_title", "Welcome back.")}
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--qt-text-2)" }}>
            {mode === "signup"
              ? (step === 1
                  ? t("auth.signup_subtitle", "Free accounts get one free preview scan per address. Upgrade for unlimited deep-dive analysis, continuous monitoring, and watermark-free reports.")
                  : t("auth.signup_persona_subtitle", "We'll use this to personalize onboarding, docs, and support — nothing more."))
              : t("auth.signin_subtitle", "Sign in to manage your plan, view invoices, and run unlimited scans on your tier.")}
          </p>

          {/* STEP 1 — credentials (shared form) */}
          {(mode === "login" || step === 1) && (
            <SignInForm
              testidPrefix="signin"
              mode={mode}
              setMode={(next) => { setMode(next); setStep(1); }}
              onLogin={onLogin}
              onSignupSubmit={onSignupSubmit}
              onMagicTrusted={onMagicTrusted}
              magicError={magicError}
              signupSubmitLabel="Continue"
              className="mt-6"
            />
          )}

          {/* STEP 2 — persona wizard */}
          {mode === "signup" && step === 2 && (
            <div className="mt-6">
              <PersonaStep
                testid="signup-persona"
                submitLabel="Create account"
                busy={busy}
                error={step2Error}
                onSubmit={submitStep2}
              />
              <div className="mt-3 text-xs">
                <button
                  type="button"
                  onClick={() => { setStep(1); setStep2Error(""); }}
                  data-testid="signup-back-btn"
                  className="qt-mono"
                  style={{ color: "var(--qt-text-3)", letterSpacing: "0.05em" }}
                >
                  ← Back
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
